// CSRF for AJAX — auto-attaches X-CSRF-Token to same-origin state-changing requests.
//
// Reads the per-session token from <meta name="csrf-token"> in the page head
// (emitted server-side by vanilla::csrfMeta()) and injects it as the
// X-CSRF-Token header on every same-origin POST/PUT/PATCH/DELETE made via:
//   • window.fetch()
//   • XMLHttpRequest (covers jQuery $.ajax, axios, and anything else built on XHR)
//
// Server side: AJAX endpoints verify with vanilla::csrfVerifyAjax().
// See _README/csrf.readme.md for the full pattern.

const STATE_CHANGING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function readToken(): string {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
    return meta?.content ?? '';
}

function isSameOrigin(url: string): boolean {
    try {
        return new URL(url, window.location.href).origin === window.location.origin;
    } catch {
        return true;
    }
}

function install(token: string): void {
    // ── fetch() wrapper ──────────────────────────────────────────────────────
    if (typeof window.fetch === 'function') {
        const originalFetch = window.fetch.bind(window);
        window.fetch = (input: RequestInfo | URL, init: RequestInit = {}) => {
            const url = typeof input === 'string' || input instanceof URL
                ? input.toString()
                : input.url;
            const method = (
                init.method
                ?? (typeof input !== 'string' && !(input instanceof URL) ? input.method : 'GET')
                ?? 'GET'
            ).toUpperCase();
            if (STATE_CHANGING.has(method) && isSameOrigin(url)) {
                const headers = new Headers(
                    init.headers
                    ?? (typeof input !== 'string' && !(input instanceof URL) ? input.headers : undefined),
                );
                if (!headers.has('X-CSRF-Token')) {
                    headers.set('X-CSRF-Token', token);
                }
                init.headers = headers;
            }
            return originalFetch(input, init);
        };
    }

    // ── XMLHttpRequest wrapper ──────────────────────────────────────────────
    // Covers jQuery $.ajax, axios, and any other XHR-based client without
    // needing each one to register a separate prefilter/interceptor.
    const OriginalXHR = window.XMLHttpRequest;
    if (OriginalXHR) {
        const open = OriginalXHR.prototype.open;
        const send = OriginalXHR.prototype.send;
        OriginalXHR.prototype.open = function (
            this: XMLHttpRequest & { __csrfMethod?: string; __csrfUrl?: string },
            method: string,
            url: string | URL,
            ...rest: unknown[]
        ): void {
            this.__csrfMethod = method.toUpperCase();
            this.__csrfUrl = url.toString();
            // @ts-expect-error — forwarding the remaining open() args verbatim
            return open.call(this, method, url, ...rest);
        };
        OriginalXHR.prototype.send = function (
            this: XMLHttpRequest & { __csrfMethod?: string; __csrfUrl?: string },
            body?: Document | XMLHttpRequestBodyInit | null,
        ): void {
            if (
                this.__csrfMethod
                && STATE_CHANGING.has(this.__csrfMethod)
                && this.__csrfUrl
                && isSameOrigin(this.__csrfUrl)
            ) {
                try {
                    this.setRequestHeader('X-CSRF-Token', token);
                } catch {
                    // setRequestHeader throws if called in the wrong readyState
                    // (e.g. caller already set their own headers and we're racing).
                    // Safe to swallow — server will reject the request and the
                    // caller will see a 419.
                }
            }
            return send.call(this, body);
        };
    }

    // Expose for code that builds requests in ways we don't intercept.
    (window as unknown as { csrfToken: string }).csrfToken = token;
}

function init(): void {
    const token = readToken();
    if (!token) {
        return;
    }
    install(token);
}

export const bseeCsrf = { init };

// Run as early as possible — script tag is in <head>, so DOM may not be ready
// when this module evaluates. Wait for DOMContentLoaded so the meta tag exists.
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
